import React from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { CurrencySelector } from "../CurrencySelector";

const navItems = [
  { label: "Home", path: "/" },
  { label: "About", path: "/about" },
  { label: "Student Portal", path: "/login", highlight: true },
  { label: "Academic Scholarship", path: "/music-scholarship" },
  { label: "Instruments & Levels", path: "/instruments-levels" },
  { label: "Examination System", path: "/examination-system" },
  { label: "Teachers & Educators", path: "/teachers-educators" },
  { label: "Become an Authorized Teacher", path: "/authorized-teacher" },
  { label: "Teacher Resources", path: "/teacher-resources" },
  { label: "Assessment Criteria", path: "/assessment-criteria" },
  { label: "Certification & Recognition", path: "/certification-recognition" },
  { label: "Examiner Panel", path: "/examiner-panel" },
  { label: "Fees & Schedule", path: "/fees-schedule" },
  { label: "FAQs", path: "/faqs" },
  { label: "Contact & Support", path: "/contact-support" },
];

const Header = () => {
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-[#d9c9aa] bg-gradient-to-b from-[#111729] to-[#151c33] text-[#f8f2e6]">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 lg:px-6 lg:py-4">
        <Link to="/" className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm overflow-hidden md:h-14 md:w-14">
            <img
              src="https://customer-assets.emergentagent.com/job_global-music-exams/artifacts/a1h5nluh_GMC-Logo-removbg.jpg"
              alt="Global Music Conservatory logo"
              className="h-[140%] w-[140%] object-cover scale-100"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-200">
              Global Music Conservatory
            </span>
            <span className="text-[10px] sm:text-[11px] font-medium italic tracking-wide text-[#d5c39d] block">
              Certified Music Excellence with Global Recognition.
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <CurrencySelector />
          <Button
            asChild
            size="sm"
            className="border border-amber-300 bg-amber-400/90 px-4 text-xs font-semibold uppercase tracking-[0.16em] text-[#1a2136] shadow-sm hover:bg-amber-400 hidden sm:inline-flex"
          >
            <Link to="/login">Student Portal</Link>
          </Button>
          <MobileNav />
        </div>
      </div>
    </header>
  );
};

const MobileNav = () => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9 border-slate-300">
          <Menu className="h-4 w-4" />
          <span className="sr-only">Open navigation</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-xs border-l border-[#d1c3a4] bg-[#f7f1e6] backdrop-blur-xl">
        <div className="mb-4 flex items-center justify-between pt-2">
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
            Navigation
          </span>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-slate-900">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex flex-col gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `rounded-md px-2 py-2 text-sm font-medium transition-colors duration-150 ${
                  item.highlight
                    ? isActive
                      ? "bg-amber-100 text-amber-900 font-semibold"
                      : "bg-amber-50 text-amber-800 hover:bg-amber-100 font-semibold"
                    : isActive
                    ? "bg-sky-50 text-sky-900"
                    : "text-slate-700 hover:bg-slate-50 hover:text-sky-800"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Footer = () => {
  return (
    <footer className="border-t border-[#d1c3a4] bg-[#111729]">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 text-xs text-[#e4dcc9] md:flex-row md:items-center md:justify-between lg:px-6">
        <p>
          © {new Date().getFullYear()} Global Music Conservatory. All rights reserved.
        </p>
        <div className="flex flex-wrap gap-4">
          <span>International Music Certification Programme</span>
          <span className="h-3 w-px bg-[#d1c3a4]" aria-hidden="true" />
          <span>Independent External Assessment</span>
        </div>
      </div>
    </footer>
  );
};

export const MainLayout = () => {
  return (
    <div className="flex min-h-screen flex-col bg-[#f6efe3]">
      <Header />
      <main className="flex-1">
        <div className="mx-auto max-w-6xl px-4 py-8 lg:px-6 lg:py-10">
          <Outlet />
        </div>
      </main>
      <Footer />
    </div>
  );
};
